<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Breadcrumb -->
      <nav class="flex items-center gap-2 text-sm mb-6">
        <router-link to="/organisation" class="text-gray-500 hover:text-[#22282F] transition-colors">
          Organisation
        </router-link>
        <ChevronRight class="w-4 h-4 text-gray-400" />
        <span class="text-[#22282F] font-medium">{{ direction?.nom }}</span>
      </nav>

      <!-- Direction Info Card -->
      <div class="bg-gradient-to-r from-[#22282F] to-[#22282F]/80 text-white rounded-xl p-6 mb-6 shadow-lg">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <div class="p-3 bg-white/10 rounded-lg">
              <Building2 class="w-8 h-8" />
            </div>
            <div>
              <h1 class="text-2xl font-bold">{{ direction?.nom }}</h1>
              <p class="text-white/70 text-sm">{{ direction?.code }}</p>
            </div>
          </div>
          <button
            @click="openCreateModal"
            class="px-4 py-2 bg-white text-[#22282F] rounded-lg hover:bg-white/90 transition-colors flex items-center gap-2"
          >
            <Plus class="w-4 h-4" />
            Nouveau Département
          </button>
        </div>
      </div>

      <!-- Search Bar -->
      <div class="mb-6 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div class="relative">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Rechercher un département par nom ou code..."
            class="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
          />
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-12 bg-white border border-gray-200 rounded-xl">
        <div class="w-8 h-8 border-4 border-gray-200 border-t-[#22282F] rounded-full animate-spin" />
      </div>

      <!-- Départements Grid -->
      <div v-else-if="filteredDepartements.length" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="departement in filteredDepartements"
          :key="departement.id"
          class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer group"
          @click="goToServices(departement)"
        >
          <div class="flex items-start justify-between mb-4">
            <div class="p-3 bg-gray-50 rounded-lg group-hover:bg-[#22282F] group-hover:text-white transition-colors">
              <Layers class="w-6 h-6" />
            </div>
            <div class="flex gap-2">
              <button
                @click.stop="openEditModal(departement)"
                class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Modifier"
              >
                <Edit3 class="w-4 h-4 text-gray-600" />
              </button>
              <button
                @click.stop="deleteDepartement(departement.id)"
                class="p-2 hover:bg-red-50 rounded-lg transition-colors"
                title="Supprimer"
              >
                <Trash2 class="w-4 h-4 text-red-600" />
              </button>
            </div>
          </div>

          <h3 class="text-lg font-semibold text-[#22282F] mb-1">{{ departement.nom }}</h3>
          <p class="text-sm text-gray-500 mb-4">{{ departement.code }}</p>

          <div class="flex items-center justify-between pt-4 border-t border-gray-100">
            <div class="flex items-center gap-2 text-sm text-gray-600">
              <Users class="w-4 h-4" />
              <span>{{ departement.services_count || 0 }} service(s)</span>
            </div>
            <ChevronRight class="w-5 h-5 text-gray-400 group-hover:text-[#22282F] transition-colors" />
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="bg-white border border-gray-200 rounded-xl p-12 text-center">
        <Layers class="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 class="text-lg font-semibold text-[#22282F] mb-2">Aucun département trouvé</h3>
        <p class="text-sm text-gray-500">
          {{ searchQuery ? 'Essayez de modifier votre recherche' : 'Créez le premier département de cette direction' }}
        </p>
      </div>

      <!-- Modal -->
      <div
        v-if="showModal"
        class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
        @click.self="closeModal"
      >
        <div class="bg-white rounded-xl max-w-md w-full p-6">
          <h3 class="text-lg font-bold text-[#22282F] mb-4">
            {{ editingDepartement ? 'Modifier le département' : 'Nouveau département' }}
          </h3>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-[#22282F] mb-2">Code</label>
              <input
                v-model="form.code"
                type="text"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                placeholder="ex: DEPT-01"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-[#22282F] mb-2">Nom</label>
              <input
                v-model="form.nom"
                type="text"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
                placeholder="ex: Département Administratif"
              />
            </div>
          </div>
          <div class="flex gap-3 mt-6">
            <button
              @click="closeModal"
              class="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              @click="saveDepartement"
              class="flex-1 px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#22282F]/90 transition-colors"
            >
              {{ editingDepartement ? 'Modifier' : 'Créer' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Building2, Layers, Users, Plus, Edit3, Trash2, Search, ChevronRight } from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'

const toast = useToast()

interface Direction {
  id: number
  code: string
  nom: string
}

interface Departement {
  id: number
  code: string
  nom: string
  services_count?: number
  direction: Direction
}

const router = useRouter()
const route = useRoute()
const directionId = ref(Number(route.params.directionId))

const loading = ref(false)
const direction = ref<Direction | null>(null)
const departements = ref<Departement[]>([])
const searchQuery = ref('')
const showModal = ref(false)
const editingDepartement = ref<Departement | null>(null)
const form = ref({ code: '', nom: '' })

const filteredDepartements = computed(() => {
  if (!searchQuery.value) return departements.value
  const query = searchQuery.value.toLowerCase()
  return departements.value.filter(d =>
    d.nom.toLowerCase().includes(query) || d.code.toLowerCase().includes(query)
  )
})

const fetchDirection = async () => {
  try {
    direction.value = await apiFetch(`/api/admin/directions/${directionId.value}/`)
  } catch (error: any) {
    toast.error('Erreur', 'Impossible de charger la direction')
  }
}

const fetchDepartements = async () => {
  loading.value = true
  try {
    const allDepartements = await apiFetch('/api/admin/departements/')
    departements.value = allDepartements.filter((d: Departement) => d.direction?.id === directionId.value)
  } catch (error: any) {
    toast.error('Erreur', 'Impossible de charger les départements')
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  editingDepartement.value = null
  form.value = { code: '', nom: '' }
  showModal.value = true
}

const openEditModal = (departement: Departement) => {
  editingDepartement.value = departement
  form.value = { code: departement.code, nom: departement.nom }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingDepartement.value = null
}

const saveDepartement = async () => {
  try {
    const payload = {
      ...form.value,
      direction_id: directionId.value
    }

    const url = editingDepartement.value
      ? `/api/admin/departements/${editingDepartement.value.id}/update/`
      : '/api/admin/departements/create/'

    await apiFetch(url, {
      method: editingDepartement.value ? 'PUT' : 'POST',
      body: JSON.stringify(payload)
    })

    toast.success(
      editingDepartement.value ? 'Département modifié' : 'Département créé',
      'L\'opération a réussi'
    )
    await fetchDepartements()
    closeModal()
  } catch (error: any) {
    toast.error('Erreur', error.message || 'Impossible de sauvegarder le département')
  }
}

const deleteDepartement = async (id: number) => {
  try {
    await apiFetch(`/api/admin/departements/${id}/delete/`, {
      method: 'DELETE'
    })

    toast.success('Département supprimé', 'Le département a été supprimé avec succès')
    await fetchDepartements()
  } catch (error: any) {
    toast.error('Erreur', error.message || 'Impossible de supprimer le département')
  }
}

const goToServices = (departement: Departement) => {
  router.push(`/organisation/directions/${directionId.value}/departements/${departement.id}/services`)
}

onMounted(() => {
  fetchDirection()
  fetchDepartements()
})
</script>
